import _ from 'lodash'
import axios from 'axios'
import moment from 'moment'
import styled, { keyframes } from 'styled-components'
import { useController } from 'react-hook-form'
import { useRef } from 'react'

import api from '../services/api'
import useModal from '../hooks/useModal'
import Utils from '../utils'

import theme from '../styles/theme'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'

import { ReactComponent as ImgIcon } from '../assets/images/image_icon.svg'

/**
 * 기본 버튼
 */
export const BasicBtn = styled.button`
  color: white;
  cursor: pointer;
  background-color: ${props => props.theme.colors.primary};
  border: none;
  border-radius: 10px;
  font-size: ${props => props.fontSize};
  height: ${props => props.height};
  width: ${props => props.width};
  line-height:  ${props => props.lineHeight};
  margin: ${props => props.margin};
  text-align: center;
`

/**
 * 취소 버튼
 */
export const CancelBtn = styled(BasicBtn)`
  color: ${props => props.theme.colors.primary};
  background-color: #FFFFFF;
  border-color: ${props => props.theme.colors.primary};
  border-style: solid;
  font-weight: 700;
`

/**
 * 확인 및 완료 버튼
 */
export const CompleteBtn = styled(BasicBtn)`
  color: ${props => props.disabled ? props.theme.colors.gray : '#FFFFFF'};
  cursor: ${props => props.disabled ? 'default' : 'pointer' };
  background-color: ${props => props.disabled ? props.theme.colors.lightGray : null};
  font-weight: 700;
`

/**
 * 출석 인증 버튼
 */
const AttendanceBtn = styled.button`
  color: ${props => props.theme.colors.lightGray};
  cursor: pointer;
  background: url(${props => props.imgUrl});
  background-size: cover;
  border-color: ${props => props.theme.colors.lightGray};
  border-radius: 10px;
  border-style: solid;
  border-width: 1px;
  display: block;
  height: ${props => props.height};
  width: ${props => props.width};
  margin: 10px auto;
`

const flowBlue = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`;

export const AiMentoringBtn = styled(BasicBtn)`
    background: linear-gradient(
            135deg,
            #eef2ff 0%,
            #dbeafe 40%,
            #e0e7ff 70%,
            #f0f9ff 100%
    );
    background-size: 200% 200%;
    animation: ${flowBlue} 5s ease-in-out infinite;
    border: 1px solid rgba(59, 130, 246, 0.25);
    color: #1e3a8a;
    font-weight: 800;
    font-size: 13px;
    box-shadow: 0 2px 10px rgba(186, 204, 255, 0.3);
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        border: 1px solid rgba(59, 130, 246, 0.5);
        transform: translateY(-1px);
        box-shadow: 0 4px 15px rgba(186, 204, 255, 0.5);
        background-size: 150% 150%;
    }

    &:active {
        transform: scale(0.96);
        filter: brightness(0.95);
    }
`;

/**
 * 출석 인증 추가/삭제 버튼
 */
export function UpdateAttendanceBtn({ proofOn, height, width, attendanceInfo, setAttendanceInfo, setDailyLogContentsList, setDailyLogList, limit, currentPage }) {
  const { openModal } = useModal()
  const selectFile = useRef('')

  function cancelAttendance(proofOn) {
    try {
      api.delete(`attendances/${proofOn}`).then(() => setAttendanceInfo(null))
    } catch (err) {
      console.error(err)
      openModal({ type: 'alert', props: { isError: true, contents: err.response?.data.message || err.message }})
    }
  }

  async function handleFileChange(e) {
    const file = e.target.files[0]
    const fileType = file.name.split('.').pop()

    if (fileType !== 'jpg' && fileType !== 'jpeg' && fileType !== 'png') {
      return openModal({ type: 'alert', props: { isError: true, contents: 'jpg, jpeg, png 파일만 업로드 가능합니다' }})
    }

    const fileName = `${moment().unix()}.${fileType}`

    try {
      const signedUrl = await api.get(`/contents/signed-urls/attendance/${fileName}`).then(result => result.data.signedUrl)
      if (! signedUrl) return
      await axios.put(signedUrl, file, { headers: { 'Content-Type': 'image/*'} })
      const attendanceResult = await api.post(`/attendances/${proofOn}`
        , { imgUrl: `${process.env.REACT_APP_CLOUDFRONT_URL}/attendance-proof/${fileName}` })
        .then(result => result.data)
      setAttendanceInfo(attendanceResult)

      if (setDailyLogContentsList) {
        const { createdAt } = attendanceResult
        await api.post(`/daily-logs/${proofOn}`, {
          startOn: createdAt,
          endOn: moment(createdAt).add(1, 'second').format('YYYY-MM-DD HH:mm:ss'),
          dailyLogTypeId: Utils.getDailyLogTypeIdByTypeNameEn('attendance'),
          contents: '출석',
        })
        .then(result => setDailyLogContentsList(result.data.dailyLogContentsList))
      }

      if (setDailyLogList) {
        await api.get('/daily-logs', { params: { page: currentPage, limit, }})
          .then(result => setDailyLogList(result.data))
      }
    } catch (err) {
      console.error(err)
      openModal({ type: 'alert', props: { isError: true, contents: err.response?.data.message || err.message }})
    }
  }

  return (
    <>
      {
        _.isEmpty(attendanceInfo)
          ?
          <AttendanceBtn
            height={height}
            width={width}
            onClick={() => selectFile.current.click()}>
            <p>출석 인증을 해주세요</p>
            <ImgIcon width='40%' fill={theme.colors.lightGray}/>
            <input
              accept='image/jpg, image/png, image/jpeg'
              onChange={handleFileChange}
              ref={selectFile}
              type='file'
              style={{ display: 'none' }}
            />
          </AttendanceBtn>
          :
          <AttendanceBtn
            height={height}
            width={width}
            imgUrl={attendanceInfo.imgUrl}
            onClick={() => {
              openModal({
                type: 'confirm',
                props: {
                  contents: '정말로 출석 인증을 취소하시겠습니까?',
                  callbackFun: cancelAttendance,
                  itemId: proofOn,
                }
              })
            }}
          />
      }
    </>
  )
}

export function BasicRadioButtonsGroup({ control, name, defaultValue, itemList }) {
  const { field } = useController({ control, name, defaultValue })

  return (
    <FormControl>
      <RadioGroup
        row
        aria-labelledby='demo-row-radio-buttons-group-label'
        name='row-radio-buttons-group'
        defaultValue={field.value}
        onChange={field.onChange}
      >
        {
          itemList.map(item => {
            return <FormControlLabel value={item.type} control={<Radio size='small'/>} label={item.nameKo} />
          })
        }
      </RadioGroup>
    </FormControl>
  )
}